import "./styles.scss";
import * as React from "react";
import { DropdownItemProps, Modal, Table } from "semantic-ui-react";
import {
  SegmentationDetail,
  PlayerCardTemplateDefault,
  PlayerCardThemeDefault,
  PlayerCardTemplate,
  PlayerCardTheme,
  PlayerCardDefault,
  PlayerCardServices,
  SegmentationDetailServices,
} from "../../../services/bedrock";
import { useContext, useEffect, useState } from "react";
import _ from "lodash";
import DefaultDropdown from "./DefaultDropdown";
import { PlayerCardSettingsContext } from "../Contexts";

interface ManageDefaultsModalProps {
  open: boolean;
  onClose: () => void;
}

const ManageDefaultsModal: React.FC<ManageDefaultsModalProps> = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [segmentationDetails, setSegmentationDetails] = useState<SegmentationDetail[]>([]);
  const [templateDefaults, setTemplateDefaults] = useState<PlayerCardTemplateDefault[]>([]);
  const [themeDefaults, setThemeDefaults] = useState<PlayerCardThemeDefault[]>([]);
  const [templates, setTemplates] = useState<PlayerCardTemplate[]>([]);
  const [themes, setThemes] = useState<PlayerCardTheme[]>([]);

  const { Toast } = useContext(PlayerCardSettingsContext);

  const loadTemplateDefaults = async (): Promise<any> => {
    try {
      const response = await PlayerCardServices.getTemplateDefaults(1, 2000);
      return setTemplateDefaults(response.content);
    } catch (e) {
      Toast.showError(e, "Failed to get template defaults");
    }
  };

  const loadThemeDefaults = async (): Promise<any> => {
    try {
      const response = await PlayerCardServices.getThemeDefaults(1, 2000);
      return setThemeDefaults(response.content);
    } catch (e) {
      Toast.showError(e, "Failed to get theme defaults");
    }
  };

  useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    const segmentationDetailsPromise = SegmentationDetailServices.getSegmentationDetails(
      ["BRANCH", "REGION"],
      undefined,
      1,
      2000
    )
      .then(response => setSegmentationDetails(response.content))
      .catch(console.error);
    const templatesPromise = PlayerCardServices.getTemplates(undefined, 1, 2000)
      .then(response => setTemplates(response.content))
      .catch(console.error);
    const themesPromise = PlayerCardServices.getThemes(undefined, -1, 1, 2000)
      .then(response => setThemes(response.content))
      .catch(console.error);

    Promise.all([
      loadTemplateDefaults(),
      loadThemeDefaults(),
      segmentationDetailsPromise,
      templatesPromise,
      themesPromise,
    ]).finally(() => setIsLoading(false));
  }, [open]);

  const groupedSegmentationDetails = _.groupBy(segmentationDetails, "type");

  const defaultRecordDropdown = (type: "Template" | "Theme", segmentationDetailId?: number) => {
    const defaultRecords: PlayerCardDefault[] = type === "Template" ? templateDefaults : themeDefaults;
    const defaultRecord = defaultRecords.find(d => d.segmentationDetailId === (segmentationDetailId ?? null));
    const records = type === "Template" ? templates : themes;

    const options = records.map(record => {
      return {
        key: record.id,
        text: record.name,
        value: record.id,
      } as DropdownItemProps;
    });

    return (
      <Table.Cell>
        <DefaultDropdown
          type={type}
          segmentationDetailId={segmentationDetailId}
          defaultRecord={defaultRecord}
          options={options}
          onChange={type === "Template" ? loadTemplateDefaults : loadThemeDefaults}
        />
      </Table.Cell>
    );
  };

  return (
    <Modal open={open} size="large" className="playerCardDefaultsModal" onClose={onClose} closeIcon>
      <Modal.Header>Player Card Defaults</Modal.Header>
      <Modal.Content>
        {isLoading && <div>Loading...</div>}
        {!isLoading && (
          <>
            <Table compact fixed attached>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell />
                  <Table.HeaderCell>Template</Table.HeaderCell>
                  <Table.HeaderCell>Theme</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Company</Table.Cell>
                  {defaultRecordDropdown("Template")}
                  {defaultRecordDropdown("Theme")}
                </Table.Row>
              </Table.Body>
            </Table>
            {Object.keys(groupedSegmentationDetails)
              .sort()
              .reverse()
              .map(segmentationType => {
                return (
                  <Table key={segmentationType} compact attached fixed>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell colSpan="3">
                          {_.startCase(segmentationType.toLowerCase())} Defaults
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {groupedSegmentationDetails[segmentationType].map(segmentationDetail => {
                        return (
                          <Table.Row key={segmentationDetail.id}>
                            <Table.Cell>{segmentationDetail.name}</Table.Cell>
                            {defaultRecordDropdown("Template", segmentationDetail.id)}
                            {defaultRecordDropdown("Theme", segmentationDetail.id)}
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                );
              })}
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};
export default ManageDefaultsModal;
